import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '@/components/core';
import {
  actionsColumn,
  appliedDateColumn,
  departmentColumn,
  DepartmentOption,
  statusColumn,
  textColumn,
  withCard,
} from '@/modules/service-desk/shared/components/column-helpers';
import {
  canDeleteNocAsAdmin,
  canEditNoc,
  canGenerateNoc,
  canReviewNoc,
  formatLetterDateOnly,
  NOC_COPY_TYPES,
} from '../../utils/noc-rules';
import { NocApplication, NocType } from '../../types/noc.types';

type Translate = (key: string) => string;

/** Legacy `NocApplicationStatus`. */
const STATUSES: NocApplication['Status'][] = ['Pending', 'Accepted', 'Rejected', 'Generated'];

/**
 * The type's name is looked up from `NocTypeId`: filtered by type, not sortable by name. The copy
 * type ("(Hard copy)") is shown under it, as in the legacy lists.
 */
const typeColumn = (t: Translate, nocTypes: NocType[]): ColumnDef<NocApplication> => ({
  ...textColumn<NocApplication>(
    t,
    'NocTypeId',
    'NOC_TYPE',
    (row) => nocTypes.find((type) => type.ItemId === row.NocTypeId)?.Name,
    {
      filter: {
        type: 'options',
        options: nocTypes.map((type) => ({ value: type.ItemId, label: type.Name })),
      },
      sortable: false,
    }
  ),
  cell: ({ row, getValue }) => {
    const copyType = NOC_COPY_TYPES.find((option) => option.value === row.original.NocCopyType);
    return (
      <div className="flex flex-col">
        <span>{getValue<string>()}</span>
        {copyType && (
          <span className="text-xs text-muted-foreground">({t(copyType.labelKey)})</span>
        )}
      </div>
    );
  },
});

/**
 * The applicant's reason, cut to two lines in the table (the details dialog shows all of it); in
 * full in the phone card's footer.
 */
const reasonColumn = (t: Translate): ColumnDef<NocApplication> => ({
  ...textColumn<NocApplication>(t, 'Reason', 'REASON', (row) => row.Reason, { mobile: 'footer' }),
  cell: ({ getValue }) => (
    <span className="whitespace-normal break-words md:line-clamp-2 md:max-w-64">
      {getValue<string>()}
    </span>
  ),
});

/** Start - end of the trip, sorted and filtered by its (date-only) start day. */
const travelDatesColumn = (t: Translate): ColumnDef<NocApplication> => ({
  id: 'TravelStartDate',
  meta: { filter: { type: 'date' } },
  header: ({ column }) => <DataTableColumnHeader column={column} title={t('TRAVEL_DATES')} />,
  accessorFn: (row) =>
    row.TravelStartDate || row.TravelEndDate
      ? `${formatLetterDateOnly(row.TravelStartDate)} - ${formatLetterDateOnly(row.TravelEndDate)}`
      : '-',
});

interface MyColumnsProps {
  t: Translate;
  nocTypes: NocType[];
  canApply: boolean;
  canWithdraw: boolean;
  onViewDetails: (application: NocApplication) => void;
  onEdit: (application: NocApplication) => void;
  onWithdraw: (application: NocApplication) => void;
}

export const createMyNocColumns = ({
  t,
  nocTypes,
  canApply,
  canWithdraw,
  onViewDetails,
  onEdit,
  onWithdraw,
}: MyColumnsProps): ColumnDef<NocApplication>[] => [
  // Card: the NOC type is the title and the actions menu the top right. The body runs
  // Request Date | Status, Passport No | NOC Number, Travel Dates, with the reason in the footer.
  withCard(appliedDateColumn<NocApplication>(t, 'REQUEST_DATE'), {
    mobile: 'body',
    mobileOrder: 0,
  }),
  withCard(typeColumn(t, nocTypes), { mobile: 'title' }),
  withCard(
    textColumn<NocApplication>(t, 'PassportNumber', 'PASSPORT_NO', (row) => row.PassportNumber),
    { mobile: 'body', mobileOrder: 2 }
  ),
  withCard(travelDatesColumn(t), { mobile: 'full', mobileOrder: 4 }),
  reasonColumn(t),
  withCard(
    textColumn<NocApplication>(t, 'DocumentNumber', 'NOC_NUMBER', (row) => row.DocumentNumber),
    { mobile: 'body', mobileOrder: 3 }
  ),
  withCard(statusColumn<NocApplication>(t, STATUSES), { mobile: 'body', mobileOrder: 1 }),
  actionsColumn<NocApplication>(t, (application) => [
    { label: t('VIEW_DETAILS'), onSelect: () => onViewDetails(application) },
    {
      label: t('EDIT'),
      onSelect: () => onEdit(application),
      hidden: !canApply || !canEditNoc(application),
    },
    {
      label: t('WITHDRAW_NOC'),
      onSelect: () => onWithdraw(application),
      hidden: !canWithdraw || application.Status !== 'Pending',
      destructive: true,
    },
  ]),
];

interface RequestColumnsProps {
  t: Translate;
  nocTypes: NocType[];
  /** The departments the reviewer may see, for the Department column and its filter. */
  departments: readonly DepartmentOption[];
  canReview: boolean;
  canGenerate: boolean;
  canDelete: boolean;
  onApprove: (application: NocApplication) => void;
  onReject: (application: NocApplication) => void;
  onGenerate: (application: NocApplication) => void;
  onDelete: (application: NocApplication) => void;
}

export const createNocRequestColumns = ({
  t,
  nocTypes,
  departments,
  canReview,
  canGenerate,
  canDelete,
  onApprove,
  onReject,
  onGenerate,
  onDelete,
}: RequestColumnsProps): ColumnDef<NocApplication>[] => [
  // Card: the applicant's name is the title and the actions menu the top right. The body runs
  // Requested On | Status, Designation | Department, NOC Type | NOC Number, Travel Dates, with
  // the reason in the footer.
  withCard(appliedDateColumn<NocApplication>(t, 'REQUESTED_ON'), {
    mobile: 'body',
    mobileOrder: 0,
  }),
  withCard(
    textColumn<NocApplication>(t, 'Name', 'NAME', (row) => row.Name),
    { mobile: 'title' }
  ),
  withCard(
    textColumn<NocApplication>(t, 'Designation', 'DESIGNATION', (row) => row.Designation),
    { mobile: 'body', mobileOrder: 2 }
  ),
  withCard(departmentColumn<NocApplication>(t, departments), { mobile: 'body', mobileOrder: 3 }),
  withCard(typeColumn(t, nocTypes), { mobile: 'body', mobileOrder: 4 }),
  withCard(travelDatesColumn(t), { mobile: 'full', mobileOrder: 6 }),
  reasonColumn(t),
  withCard(
    textColumn<NocApplication>(t, 'DocumentNumber', 'NOC_NUMBER', (row) => row.DocumentNumber),
    { mobile: 'body', mobileOrder: 5 }
  ),
  withCard(statusColumn<NocApplication>(t, STATUSES), { mobile: 'body', mobileOrder: 1 }),
  actionsColumn<NocApplication>(t, (application) => [
    {
      label: t('APPROVE'),
      onSelect: () => onApprove(application),
      hidden: !canReview || !canReviewNoc(application),
    },
    {
      label: t('REJECT'),
      onSelect: () => onReject(application),
      hidden: !canReview || !canReviewNoc(application),
      destructive: true,
    },
    {
      label: t('GENERATE_NOC'),
      onSelect: () => onGenerate(application),
      hidden: !canGenerate || !canGenerateNoc(application),
    },
    {
      label: t('DELETE'),
      onSelect: () => onDelete(application),
      hidden: !canDelete || !canDeleteNocAsAdmin(application),
      destructive: true,
    },
  ]),
];
